/**
 * Following a parse job until it ends (engine FR-E9): the service answers a
 * request at once with the job, and the job is asked about again — soon at
 * first, then less often — until it is `completed`, `refused` or `failed`.
 *
 * There is no push to the browser: the events (`prodoc.extraction.*`) are for
 * apps, and carry no content. A screen polls the job it started, and only
 * while it is open; the upload batch, which lives outside any screen, follows
 * its own jobs to the end the same way its uploads carry on.
 */

import { useEffect, useState } from 'react'

import { jobEnded, type ParseJob } from './extraction-shapes'
import { errorText } from './hooks'
import type { ProDocClient } from './prodoc-client'

/** The first wait before a job is asked about again. A test passes a few milliseconds. */
export const JOB_POLL_MS = 1500
/** How long a job is followed before the screen says it is still going and stops asking. */
export const JOB_FOLLOW_MS = 15 * 60 * 1000

/** 1×, 2×, 4×, then 8× the first wait — a quick job is seen quickly, a long scan is not asked about every second. */
export const nextPollDelay = (base: number, attempt: number): number => base * 2 ** Math.min(Math.max(attempt, 0), 3)

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) return resolve()
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => {
      clearTimeout(timer)
      resolve()
    })
  })

export interface FollowOptions {
  pollMs?: number
  /** Stop asking after this long; the answer is the job as last seen. */
  maxMs?: number
  signal?: AbortSignal
  onUpdate?: (job: ParseJob) => void
}

/**
 * Ask about a job until it ends, or until `maxMs` or the signal stops it. A
 * read that fails is tried again on the next tick (three in a row end it), so
 * a blip in the network is not reported as a failed parse.
 */
export async function followJob(client: ProDocClient, start: ParseJob, options: FollowOptions = {}): Promise<ParseJob> {
  const { pollMs = JOB_POLL_MS, maxMs = JOB_FOLLOW_MS, signal, onUpdate } = options
  let job = start
  let failures = 0
  const began = Date.now()
  for (let attempt = 0; !jobEnded(job) && !signal?.aborted && Date.now() - began < maxMs; attempt++) {
    await sleep(nextPollDelay(pollMs, attempt), signal)
    if (signal?.aborted) break
    try {
      job = await client.parsing.job(job.id)
      failures = 0
      onUpdate?.(job)
    } catch (e) {
      failures += 1
      if (failures >= 3) throw e
    }
  }
  return job
}

export interface FollowedJob {
  job: ParseJob | null
  /** Still being asked about. */
  following: boolean
  /** Asked about for `maxMs` and still not ended. */
  gaveUp: boolean
  error: string
}

/** A job, followed while the component that started it is mounted. `null` follows nothing. */
export function useFollowedJob(client: ProDocClient, start: ParseJob | null, pollMs = JOB_POLL_MS, onEnded?: (job: ParseJob) => void): FollowedJob {
  const [state, setState] = useState<FollowedJob>({ job: start, following: false, gaveUp: false, error: '' })
  const startId = start?.id ?? null
  useEffect(() => {
    if (!start) {
      setState({ job: null, following: false, gaveUp: false, error: '' })
      return
    }
    const controller = new AbortController()
    const ended = jobEnded(start)
    setState({ job: start, following: !ended, gaveUp: false, error: '' })
    if (ended) {
      onEnded?.(start)
      return
    }
    void followJob(client, start, { pollMs, signal: controller.signal, onUpdate: (job) => setState((s) => ({ ...s, job })) })
      .then((job) => {
        if (controller.signal.aborted) return
        setState({ job, following: false, gaveUp: !jobEnded(job), error: '' })
        if (jobEnded(job)) onEnded?.(job)
      })
      .catch((e) => {
        if (!controller.signal.aborted) setState((s) => ({ ...s, following: false, error: errorText(e, 'ProDoc did not say how the reading went.') }))
      })
    return () => controller.abort()
    // The job's id is the question; the callback is the caller's and may change identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, startId, pollMs])
  return state
}
